import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiUpload, FiCheck, FiArchive, FiAlertTriangle, FiPlus } from "react-icons/fi";

const NEU_RAISED_LG =
  "shadow-[-10px_-10px_22px_var(--outer-shadow-light),10px_10px_22px_var(--outer-shadow-dark)]";
const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";
const NEU_INSET_SM =
  "shadow-[inset_2px_2px_4px_var(--shadow-dark),inset_-2px_-2px_4px_var(--shadow-light)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";
const PRIMARY_PILL =
  "inline-flex items-center gap-2 h-11 px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed";
const SECONDARY_PILL = (extra = "") =>
  `inline-flex items-center gap-2 h-11 px-space-md rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all ${extra}`;
const INPUT =
  "w-full h-11 px-4 rounded-full bg-[#eef2f7] border-none text-body-md text-neu-primary placeholder:text-slate-muted shadow-[inset_3px_3px_6px_rgba(0,0,0,0.08),inset_-4px_-4px_9px_rgba(255,255,255,1)] focus:outline-none focus:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.10),inset_-4px_-4px_9px_rgba(255,255,255,1),0_0_0_2px_rgba(229,116,51,0.35)] transition-shadow";

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadErrorsCsv = (errors, slug) => {
  const header = ["row", "type", "message", "value"];
  const rows = errors.map((e) => [e.row, e.type, e.message, e.value || ""]);
  const csv = [header, ...rows]
    .map((r) => r.map(csvEscape).join(","))
    .join("\n");
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug || "reading-plan"}-import-errors-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("bibleMemosTheme") || "powder"; } catch (_e) { return "powder"; }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return theme;
};

// ================ List view =================================================

export const ReadingPlanAdminList = () => {
  const theme = useTheme();
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    slug: "",
    name: "",
    year: new Date().getFullYear(),
    plan_type: "one_year",
    timezone: "America/Chicago",
    timezone_policy: "user_local",
    source_name: "",
  });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch("/api/admin/reading-plans", { credentials: "include" });
      if (res.status === 401 || res.status === 403) {
        throw new Error("You must be signed in as an admin to manage plans.");
      }
      if (!res.ok) throw new Error("Failed to load plans");
      setPlans(await res.json());
    } catch (err) {
      setError(err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/reading-plans", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to create plan");
      navigate(`/admin/reading-plans/${body.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          <section
            className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex flex-col gap-space-xs`}
          >
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Administration
            </span>
            <h1 className="font-noto-serif text-headline-lg text-neu-primary">Reading Plans</h1>
            <nav aria-label="Admin sections" className="flex flex-wrap items-center gap-2 text-label-sm">
              <span className="text-slate-muted">Admin areas:</span>
              <Link to="/admin/devotions" className="font-semibold text-accent-cta hover:underline">Devotional Content</Link>
              <span className="text-slate-muted">·</span>
              <span className="font-semibold text-neu-primary underline">Reading Plans</span>
            </nav>
            <p className="text-body-lg text-slate-muted max-w-2xl">
              Create the next year's annual plan, upload the CSV/XLSX schedule, review the parsed
              rows, and publish once every row is clean.
            </p>
          </section>

          {error && (
            <section className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-md text-[#b02a37]`}>
              {error}
            </section>
          )}

          {/* Create draft */}
          <section
            className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD} flex flex-col gap-space-md`}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                New Plan
              </span>
              <h2 className="font-noto-serif text-headline-md text-neu-primary">Create draft</h2>
            </div>
            <form onSubmit={submitCreate} className="grid grid-cols-1 md:grid-cols-2 gap-space-md">
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Slug (URL identifier)</span>
                <input required className={INPUT} value={draft.slug}
                  onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                  placeholder="one-year-2027" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Display name</span>
                <input required className={INPUT} value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="2027 일년일독 통독표" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Year</span>
                <input required type="number" className={INPUT} value={draft.year}
                  onChange={(e) => setDraft({ ...draft, year: Number(e.target.value) })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Plan type</span>
                <select className={INPUT} value={draft.plan_type}
                  onChange={(e) => setDraft({ ...draft, plan_type: e.target.value })}>
                  <option value="one_year">One-year (통독)</option>
                  <option value="daily_devotion">Daily devotional (매일성경)</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Default timezone (IANA)</span>
                <input className={INPUT} value={draft.timezone}
                  onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                  placeholder="America/Chicago" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-label-sm text-slate-muted">Timezone policy</span>
                <select className={INPUT} value={draft.timezone_policy}
                  onChange={(e) => setDraft({ ...draft, timezone_policy: e.target.value })}>
                  <option value="user_local">User's local timezone</option>
                  <option value="fixed_plan_timezone">Fixed plan timezone</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-label-sm text-slate-muted">Source name (attribution)</span>
                <input className={INPUT} value={draft.source_name}
                  onChange={(e) => setDraft({ ...draft, source_name: e.target.value })}
                  placeholder="Scripture Union Korea · 매일성경 통독표" />
              </label>
              <div className="md:col-span-2 flex items-center gap-space-sm">
                <button type="submit" className={PRIMARY_PILL} disabled={creating}>
                  <FiPlus />
                  <span>{creating ? "Creating…" : "Create Draft"}</span>
                </button>
              </div>
            </form>
          </section>

          {/* Existing plans */}
          <section className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD} flex flex-col gap-space-md`}>
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                All plans
              </span>
              <h2 className="font-noto-serif text-headline-md text-neu-primary">Manage schedules</h2>
            </div>
            {plans === null && <p className="text-body-md text-slate-muted">Loading…</p>}
            {plans?.length === 0 && (
              <p className="text-body-md text-slate-muted">No plans yet — create one above.</p>
            )}
            {plans && plans.length > 0 && (
              <ul className="flex flex-col divide-y divide-[color:var(--outer-shadow-dark)]/20">
                {plans.map((p) => (
                  <li key={p.id} className="py-space-sm flex flex-wrap items-center justify-between gap-space-sm">
                    <div className="flex flex-col min-w-0">
                      <p className="text-label-md text-neu-primary font-semibold truncate">
                        {p.name}
                      </p>
                      <p className="text-label-sm text-slate-muted">
                        {p.slug} · {p.plan_type} · {p.year} · {p.day_count || 0} days
                      </p>
                    </div>
                    <div className="flex items-center gap-space-sm">
                      <span className={`px-space-sm py-1 rounded-full text-label-sm font-semibold ${NEU_INSET_SM} ${
                        p.status === "published" ? "text-accent-cta" :
                        p.status === "archived" ? "text-slate-muted" : "text-neu-primary"
                      }`}>
                        {p.status}
                      </span>
                      <Link to={`/admin/reading-plans/${p.id}`} className={SECONDARY_PILL()}>
                        Open
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

// ================ Plan detail + upload ======================================

export const ReadingPlanAdminDetail = () => {
  const theme = useTheme();
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/reading-plans/${planId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not load plan");
      setPlan(await res.json());
    } catch (err) {
      setError(err.message);
    }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/admin/reading-plans/${planId}/imports`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Upload failed");
      setPreview(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const doApply = async () => {
    if (!preview?.rows?.length) return;
    if (preview.errors?.length) {
      setError("Cannot apply — resolve validation errors first.");
      return;
    }
    if (!window.confirm(`Import ${preview.rows.length} rows into plan '${plan.name}'? This replaces existing schedule days for the plan.`)) return;
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/reading-plans/${planId}/imports/apply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: preview.rows }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Apply failed");
      setPreview(null);
      setFile(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const doPublish = async () => {
    if (!window.confirm(`Publish '${plan.name}'? Users will see it as the active plan.`)) return;
    setPublishing(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/reading-plans/${planId}/publish`, {
        method: "POST", credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Publish failed");
      setPlan(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  };

  const doArchive = async () => {
    if (!window.confirm(`Archive '${plan.name}'?`)) return;
    try {
      const res = await fetch(`/api/admin/reading-plans/${planId}/archive`, {
        method: "POST", credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Archive failed");
      setPlan(body);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!plan) {
    return (
      <div data-theme={theme} className="min-h-screen bg-page-bg p-space-xl text-slate-muted">
        {error ? error : "Loading…"}
      </div>
    );
  }

  const summary = preview?.summary;

  return (
    <div data-theme={theme} className="min-h-screen bg-page-bg text-on-surface font-inter">
      <main className="w-full">
        <div className="w-full max-w-[1440px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-xl">
          <section className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_LG} flex flex-col gap-space-md`}>
            <div className="flex flex-col gap-space-xs">
              <Link to="/admin/reading-plans" className="text-label-sm text-accent-cta font-semibold">
                ← All plans
              </Link>
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                {plan.plan_type} · {plan.year}
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">{plan.name}</h1>
              <p className="text-body-md text-slate-muted">
                slug: <code>{plan.slug}</code> · timezone: {plan.timezone} ({plan.timezone_policy})
                · days: {plan.day_count || 0}
                · status: <span className="font-semibold text-neu-primary">{plan.status}</span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-space-sm">
              <button type="button" onClick={doPublish} disabled={publishing || plan.status === "published" || !plan.day_count}
                className={PRIMARY_PILL}>
                <FiCheck />
                <span>{publishing ? "Publishing…" : "Publish"}</span>
              </button>
              <button type="button" onClick={doArchive}
                disabled={plan.status === "archived"} className={SECONDARY_PILL()}>
                <FiArchive />
                <span>Archive</span>
              </button>
            </div>
          </section>

          {error && (
            <section className={`rounded-[2rem] bg-canvas-base p-space-lg ${NEU_RAISED_MD} text-body-md text-[#b02a37]`}>
              {error}
            </section>
          )}

          {/* Upload */}
          <section className={`rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD} flex flex-col gap-space-md`}>
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Schedule
              </span>
              <h2 className="font-noto-serif text-headline-md text-neu-primary">Upload schedule</h2>
              <p className="text-body-md text-slate-muted">
                Required columns: <code>date</code>, <code>reference</code>. Optional:
                <code> reference_ko</code>, <code>reference_en</code>, <code>sequence</code>.
                Accepts CSV or XLSX (max 5 MB).
              </p>
            </div>
            <form onSubmit={doUpload} className="flex flex-wrap items-center gap-space-sm">
              <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-body-md text-neu-primary" />
              <button type="submit" className={PRIMARY_PILL} disabled={!file || uploading}>
                <FiUpload />
                <span>{uploading ? "Parsing…" : "Parse & Validate"}</span>
              </button>
            </form>

            {preview && (
              <div className="flex flex-col gap-space-sm">
                <div className={`p-space-md rounded-[1.25rem] bg-canvas-base ${NEU_INSET_SM} grid grid-cols-2 md:grid-cols-4 gap-2 text-label-sm`}>
                  <div><span className="text-slate-muted">Rows</span><div className="text-neu-primary font-semibold">{summary.total_rows}</div></div>
                  <div><span className="text-slate-muted">Distinct dates</span><div className="text-neu-primary font-semibold">{summary.distinct_dates}</div></div>
                  <div><span className="text-slate-muted">First</span><div className="text-neu-primary font-semibold">{summary.first_date || "—"}</div></div>
                  <div><span className="text-slate-muted">Last</span><div className="text-neu-primary font-semibold">{summary.last_date || "—"}</div></div>
                  <div><span className="text-slate-muted">Errors</span><div className={`font-semibold ${summary.error_count ? "text-[#b02a37]" : "text-accent-cta"}`}>{summary.error_count}</div></div>
                  <div><span className="text-slate-muted">Warnings</span><div className="font-semibold text-neu-primary">{summary.warning_count}</div></div>
                </div>

                {preview.errors.length > 0 && (
                  <details className={`rounded-[1rem] p-space-sm bg-canvas-base ${NEU_INSET_SM}`}>
                    <summary className="text-label-md text-[#b02a37] font-semibold cursor-pointer flex items-center gap-1">
                      <FiAlertTriangle /> Errors ({preview.errors.length})
                    </summary>
                    <div className="mt-2 flex flex-col gap-2">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => downloadErrorsCsv(preview.errors, plan?.slug)}
                          className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-canvas-base text-neu-primary text-label-sm font-semibold shadow-[-2px_-2px_4px_var(--outer-shadow-light),2px_2px_4px_var(--outer-shadow-dark)] hover:shadow-[-1px_-1px_2px_var(--outer-shadow-light),1px_1px_2px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_4px_var(--outer-shadow-dark),inset_-2px_-2px_4px_var(--outer-shadow-light)] transition-all"
                        >
                          Download errors CSV
                        </button>
                      </div>
                      <ul className="flex flex-col gap-1 text-body-md text-neu-primary max-h-80 overflow-y-auto">
                        {preview.errors.slice(0, 200).map((e, i) => (
                          <li key={i}>
                            Row {e.row}: <span className="text-[#b02a37]">{e.type}</span> — {e.message}
                            {e.value && <span className="text-slate-muted"> ({e.value})</span>}
                          </li>
                        ))}
                        {preview.errors.length > 200 && (
                          <li className="text-slate-muted">
                            …plus {preview.errors.length - 200} more (download CSV to see all).
                          </li>
                        )}
                      </ul>
                    </div>
                  </details>
                )}

                {preview.warnings.length > 0 && (
                  <details className={`rounded-[1rem] p-space-sm bg-canvas-base ${NEU_INSET_SM}`}>
                    <summary className="text-label-md text-neu-primary font-semibold cursor-pointer">
                      Warnings ({preview.warnings.length})
                    </summary>
                    <ul className="mt-2 flex flex-col gap-1 text-body-md text-slate-muted">
                      {preview.warnings.slice(0, 30).map((w, i) => (
                        <li key={i}>Row {w.row}: {w.message}{w.value ? ` (${w.value})` : ""}</li>
                      ))}
                    </ul>
                  </details>
                )}

                <div className={`rounded-[1rem] p-space-sm bg-canvas-base ${NEU_INSET_SM} max-h-80 overflow-y-auto`}>
                  <p className="text-label-sm text-slate-muted mb-2">First 20 parsed rows:</p>
                  <ul className="flex flex-col gap-1 text-body-md text-neu-primary">
                    {preview.rows.slice(0, 20).map((r, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span>{r.date} · {r.reference_ko}</span>
                        <span className="text-slate-muted">{r.reference_en}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-wrap items-center gap-space-sm">
                  <button type="button" onClick={doApply}
                    disabled={applying || summary.error_count > 0 || !preview.rows.length}
                    className={PRIMARY_PILL}>
                    <FiCheck />
                    <span>{applying ? "Applying…" : `Apply ${preview.rows.length} rows`}</span>
                  </button>
                  <button type="button" onClick={() => { setPreview(null); setFile(null); }}
                    className={SECONDARY_PILL()}>
                    Discard preview
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
